import { createContext, useReducer, useCallback, useEffect } from "react";
import axios, { setLogoutHandler } from "../../axios-orders";
import { AuthContextType, AuthState, ProviderProps } from "../types";
import { eraseCookie, getCookie, setCookie } from "../../utils";

// Constants
const TOKEN_EXPIRY_DAYS = 30; // Increase token lifetime to 30 days
const LOCAL_STORAGE_USER_ID = "userId";
const LOCAL_STORAGE_TOKEN = "accessToken";

// Initial state with both cookie and localStorage checks
const initialState: AuthState = {
  userId:
    getCookie("userId") || localStorage.getItem(LOCAL_STORAGE_USER_ID) || null,
  isAuthenticated: !!(
    getCookie("accessToken") || localStorage.getItem(LOCAL_STORAGE_TOKEN)
  ),
  error: null,
  isLoading: false,
};

// Create context
export const AuthContext = createContext<AuthContextType>({
  ...initialState,
  authSignIn: async () => {},
  authCheckState: () => {},
  logout: () => {},
});

// Auth reducer
const authReducer = (state: AuthState, action: any): AuthState => {
  switch (action.type) {
    case "AUTH_START":
      return {
        ...state,
        error: null,
        isLoading: true,
      };
    case "AUTH_SUCCESS":
      return {
        ...state,
        userId: action.userId,
        isAuthenticated: true,
        error: null,
        isLoading: false,
      };
    case "AUTH_FAIL":
      return {
        ...state,
        error: action.error,
        isLoading: false,
      };
    case "AUTH_LOGOUT":
      return {
        ...state,
        userId: null,
        isAuthenticated: false,
      };
    default:
      return state;
  }
};

// Provider component
export const AuthProvider: React.FC<ProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Setup axios authorization header on mount and when token changes
  useEffect(() => {
    const token =
      getCookie("accessToken") || localStorage.getItem(LOCAL_STORAGE_TOKEN);
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    }
  }, [state.isAuthenticated]);

  // Sign in action
  const authSignIn = useCallback(async (body: any) => {
    dispatch({ type: "AUTH_START" });

    axios
      .post("/auth/login", body)
      .then((res) => {
        if (res?.data) {
          if (res?.data.errors) {
            return dispatch({
              type: "AUTH_FAIL",
              error: res?.data.errors.message,
            });
          }

          const token = res?.data?.accessToken;
          const userId = res?.data?.userId;

          // Save to cookies (longer expiration)
          setCookie("accessToken", token, TOKEN_EXPIRY_DAYS);
          setCookie("userId", userId, TOKEN_EXPIRY_DAYS);

          // Also save to localStorage as backup
          localStorage.setItem(LOCAL_STORAGE_TOKEN, token);
          localStorage.setItem(LOCAL_STORAGE_USER_ID, userId);
          sessionStorage.removeItem("impersonationContext");

          // Set the authorization header
          axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

          dispatch({
            type: "AUTH_SUCCESS",
            accessToken: token,
            userId: userId,
            tokenType: res?.data?.tokenType,
          });
        }
      })
      .catch((error) => {
        return dispatch({
          type: "AUTH_FAIL",
          error,
        });
      });
  }, []);

  // Check auth state action
  const authCheckState = useCallback(() => {
    // Check both cookie and localStorage
    const tokenFromCookie = getCookie("accessToken");
    const tokenFromStorage = localStorage.getItem(LOCAL_STORAGE_TOKEN);
    const token = tokenFromCookie || tokenFromStorage;

    // MODIFIED: Don't automatically logout if no token is found
    // This prevents random logouts due to temporary cookie/storage issues
    if (!token) {
      // Only update authentication state, don't force logout
      // dispatch({ type: "AUTH_LOGOUT" });
      console.warn("No authentication token found, but preserving session");
      return;
    } else {
      // If token exists in localStorage but not in cookie, restore the cookie
      if (!tokenFromCookie && tokenFromStorage) {
        setCookie("accessToken", tokenFromStorage, TOKEN_EXPIRY_DAYS);
      }

      // Get userId from either cookie or localStorage
      const userIdFromCookie = getCookie("userId");
      const userIdFromStorage = localStorage.getItem(LOCAL_STORAGE_USER_ID);
      const userId = userIdFromCookie || userIdFromStorage;

      // If userId exists in localStorage but not in cookie, restore the cookie
      if (!userIdFromCookie && userIdFromStorage) {
        setCookie("userId", userIdFromStorage, TOKEN_EXPIRY_DAYS);
      }

      dispatch({
        type: "AUTH_SUCCESS",
        userId,
        token,
      });
    }
  }, []);

  // Logout action
  const logout = useCallback(() => {
    // Clear both cookies and localStorage
    eraseCookie("accessToken");
    eraseCookie("userId");
    localStorage.removeItem(LOCAL_STORAGE_TOKEN);
    localStorage.removeItem(LOCAL_STORAGE_USER_ID);
    sessionStorage.removeItem("impersonationContext");

    // Clear authorization header
    delete axios.defaults.headers.common["Authorization"];

    dispatch({ type: "AUTH_LOGOUT" });
  }, []);

  // Register logout handler with axios interceptor for 401 responses
  useEffect(() => {
    setLogoutHandler(logout);
  }, [logout]);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        authSignIn,
        authCheckState,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
